import type { ChapterSyncPhase } from '@/utils/chapterSyncPhase'

/**
 * Generic key/value storage adapter.
 * Callers (stores/components) must not depend on IndexedDB vs memory vs other backends.
 */
export interface StorageAdapter {
  get<T>(key: string): Promise<T | null>
  set<T>(key: string, value: T): Promise<void>
  delete(key: string): Promise<void>
  /** Returns all keys, optionally filtered by prefix. */
  listKeys(prefix?: string): Promise<string[]>
  clear(): Promise<void>
}

/** Persisted chapter offline backup (editor chapters only: title + jsonContent). */
export interface ChapterBackupRecord {
  projectId: string
  chapterId: string
  title: string
  /** Legacy HTML content; kept for migration / hydration of old backups. */
  content?: string
  jsonContent?: object
  timestamp: number
  savedAt: string
  sourceId: string
  /**
   * Last known server `updatedAt` when this backup was written.
   * Sent as expectedUpdatedAt on chapter patch for optimistic concurrency.
   */
  baseUpdatedAt?: string
  baseContentVersion?: number
  inDoubtSaveIds?: string[]
  /**
   * False when this record is a clean mirror of the server.
   * Missing or true means local edits still need to be uploaded.
   */
  pendingSync?: boolean
  /** Orchestrator sync state; migrated from pendingSync/retained when missing. */
  syncPhase?: ChapterSyncPhase
  /** Server sync time used for synced_grace TTL. */
  lastSuccessfulSyncAt?: number
  lastErrorCode?: string
  lastErrorMessage?: string
  /**
   * When true, age and count cleanup must not delete this copy.
   * Set for projects the user marked available offline.
   */
  retained?: boolean
  /** Chapter position in the project, when known. */
  order?: number
  /** When `title`, background PATCH omits jsonContent (title-only edits). */
  patchScope?: 'title' | 'full'
  /** Owner. Legacy records omit it and stay visible to the current user. */
  userId?: string
}

export interface StorageMetadata {
  lastCleanup: number
  version: string
  localStorageMigrated?: boolean
}

export const CHAPTER_BACKUP_KEY_PREFIX = 'chapter:'
/** User-initiated discard snapshot; separate from the live editor backup slot. */
export const CHAPTER_DISCARDED_SNAPSHOT_PREFIX = 'chapter-discarded:'
/** Server chapter copy kept during a real content-version conflict. */
export const CHAPTER_SERVER_CONFLICT_SNAPSHOT_PREFIX = 'chapter-server-conflict:'
export const STORAGE_METADATA_KEY = 'meta:chapterBackups'
export const LEGACY_LOCAL_STORAGE_PREFIX = 'escribonauta'
export const LEGACY_METADATA_KEY = 'escribonauta_metadata'
export const BACKUP_BROADCAST_CHANNEL = 'escribonauta-chapter-backups'

export type ChapterBackupBroadcastMessage =
  | {
      type: 'put'
      projectId: string
      chapterId: string
      backup: ChapterBackupRecord
    }
  | {
      type: 'remove'
      projectId: string
      chapterId: string
    }

export function chapterBackupKey(projectId: string, chapterId: string): string {
  return `${CHAPTER_BACKUP_KEY_PREFIX}${projectId}:${chapterId}`
}

export function chapterDiscardedSnapshotKey(projectId: string, chapterId: string): string {
  return `${CHAPTER_DISCARDED_SNAPSHOT_PREFIX}${projectId}:${chapterId}`
}

export function chapterServerConflictSnapshotKey(projectId: string, chapterId: string): string {
  return `${CHAPTER_SERVER_CONFLICT_SNAPSHOT_PREFIX}${projectId}:${chapterId}`
}

export function isPrimaryChapterBackupStorageKey(key: string): boolean {
  return key.startsWith(CHAPTER_BACKUP_KEY_PREFIX)
    && !key.startsWith(CHAPTER_DISCARDED_SNAPSHOT_PREFIX)
    && !key.startsWith(CHAPTER_SERVER_CONFLICT_SNAPSHOT_PREFIX)
}

export function parseChapterBackupKey(
  key: string,
): { projectId: string; chapterId: string } | null {
  if (!key.startsWith(CHAPTER_BACKUP_KEY_PREFIX)) return null
  const rest = key.slice(CHAPTER_BACKUP_KEY_PREFIX.length)
  const separatorIndex = rest.indexOf(':')
  if (separatorIndex <= 0) return null
  return {
    projectId: rest.slice(0, separatorIndex),
    chapterId: rest.slice(separatorIndex + 1),
  }
}

const OBJECT_ID = /^[a-f0-9]{24}$/i

export function isStorageObjectId(value: string): boolean {
  return OBJECT_ID.test(value)
}

/** Legacy localStorage key: escribonauta_{projectId}_{chapterId} */
export function parseLegacyLocalStorageKey(
  key: string,
): { projectId: string; chapterId: string } | null {
  if (!key.startsWith(`${LEGACY_LOCAL_STORAGE_PREFIX}_`) || key === LEGACY_METADATA_KEY) {
    return null
  }
  const withoutPrefix = key.slice(LEGACY_LOCAL_STORAGE_PREFIX.length + 1)
  const separatorIndex = withoutPrefix.indexOf('_')
  if (separatorIndex <= 0) return null
  const projectId = withoutPrefix.slice(0, separatorIndex)
  const chapterId = withoutPrefix.slice(separatorIndex + 1)
  if (!isStorageObjectId(projectId) || !isStorageObjectId(chapterId)) return null
  return { projectId, chapterId }
}

export function legacyLocalStorageKey(projectId: string, chapterId: string): string {
  return `${LEGACY_LOCAL_STORAGE_PREFIX}_${projectId}_${chapterId}`
}
